import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { SessionResponse } from '@nadoutong/contracts';
import { PlatformApiError, platformApiRequest } from '../api/client.ts';

/**
 * console-web tenant session context.
 *
 * Single source of truth for "am I logged in, and as whom". The
 * provider runs one `GET /v1/auth/session` probe at mount to hydrate.
 * Login / register / logout flows update the context directly — they
 * do NOT trigger a second session round-trip.
 *
 * The cookie itself lives in the browser jar; this context only
 * mirrors what the server told us. `clearUser` nulls the mirror AFTER
 * the caller has successfully POSTed /logout.
 *
 * Status values:
 *   - 'loading'          — initial probe pending
 *   - 'authenticated'    — /session returned 200, `user` populated
 *   - 'unauthenticated'  — /session returned 401 or network error
 */
export type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface SessionUser {
  accountId: string;
  orgId: string;
  email: string;
  emailVerified: boolean;
}

export interface SessionContextValue {
  status: SessionStatus;
  user: SessionUser | null;
  setUser: (user: SessionUser) => void;
  clearUser: () => void;
  refresh: () => Promise<void>;
  /**
   * The fetch implementation used for every platform-api call made by
   * consumers of this provider. Production callers get `window.fetch`;
   * tests inject a stub.
   */
  fetchImpl: typeof fetch;
}

const SessionContext = createContext<SessionContextValue | null>(null);

interface ProviderProps {
  children: ReactNode;
  /**
   * Injection point for tests. Production callers leave undefined and
   * the provider falls back to `window.fetch`.
   */
  fetchImpl?: typeof fetch;
}

export function SessionProvider({ children, fetchImpl }: ProviderProps): JSX.Element {
  const resolvedFetch: typeof fetch = useMemo(
    () => fetchImpl ?? ((...args) => fetch(...args)),
    [fetchImpl],
  );
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [user, setUserState] = useState<SessionUser | null>(null);

  const refresh = useCallback(async () => {
    setStatus('loading');
    try {
      const session = await platformApiRequest<SessionResponse>(
        '/v1/auth/session',
        {},
        resolvedFetch,
      );
      setUserState({
        accountId: session.accountId,
        orgId: session.orgId,
        email: session.email,
        emailVerified: session.emailVerified,
      });
      setStatus('authenticated');
    } catch (err) {
      if (err instanceof PlatformApiError && err.status === 401) {
        setUserState(null);
        setStatus('unauthenticated');
        return;
      }
      // Network / 5xx errors: fail closed so `RequireAuth` redirects
      // to /login. A dedicated "service down" banner is a follow-up
      // polish item.
      setUserState(null);
      setStatus('unauthenticated');
    }
  }, [resolvedFetch]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setUser = useCallback((next: SessionUser) => {
    setUserState(next);
    setStatus('authenticated');
  }, []);

  const clearUser = useCallback(() => {
    setUserState(null);
    setStatus('unauthenticated');
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({ status, user, setUser, clearUser, refresh, fetchImpl: resolvedFetch }),
    [status, user, setUser, clearUser, refresh, resolvedFetch],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (ctx === null) {
    throw new Error('useSession must be used inside <SessionProvider>');
  }
  return ctx;
}
